import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  Select,
  Tag,
  TimePicker,
} from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { capitalize } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/store";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import emptyUser from "../../../assets/images/empty_user.jpg";
import CustomTable from "../../../components/ui/CustomTable";
import IconButton from "../../../components/ui/IconButton";
import { BASE_API_BSRM } from "../../../constants/apiUrl";
import { API_URL_BSRM, dbDateFormat } from "../../../constants/constants";
import {
  useGetBusinessUnitQuery,
  useGetDistrictListQuery,
  useGetZoneListQuery,
} from "../../../features/bsrm/dashboard/dashboardApi";
import { useGetPaymentHistoryQuery } from "../../../features/bsrm/supplierPaymentHistory/supplierPaymentHistoryApi";
import { useGetSingleCompanyQuery } from "../../../features/portal/companyOnboard/companyOnboardApi";
import useDownloadEmployeeXlsx from "../../../hooks/useDownloadExcelWithApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { disableFutureDates } from "../../../lib/time";
import {
  ExportStatementType,
  GetSupplierPaymentHistoryParams,
  GetSupplierPaymentHistoryResponseType,
  TransactionsDetails,
} from "../../../types/bsrm/statements";
import { numberWithCommas } from "../../../utils/number";
import {
  customTimeConverter,
  formatSouthAsianNumber,
} from "../../../utils/utils";
import StatementDetailsModal from "./StatementDetailsModal";

type TimeFormat = {
  formTime?: string;
  toTime?: string;
};

export default function Statement() {
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();
  const [time, setTime] = useState<TimeFormat>();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("dealer");
  const [applyDateFilter, setApplyDateFilter] = useState({});
  const [dateError, setDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [selectedZone, setSelectedZone] = useState<string | undefined>(
    undefined
  );
  const [selectedSupplierId, setSelectedSupplierId] = useState<
    string | undefined
  >(undefined);

  const { open, handleOpen, handleClose } = useModal<
    TransactionsDetails | undefined
  >();
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetSupplierPaymentHistoryParams>();

  const { user } = useAppSelector((state) => state.auth);
  const {
    isError: isCompanyError,
    error: companyError,
    isLoading: isCompanyLoading,
    data: companyData,
  } = useGetSingleCompanyQuery(user?.data?.company_id || "", {
    skip: !user?.data?.company_id,
  });

  const selectedSupplier = companyData?.data?.dynamicKeys?.find(
    (item: any) => item.id === Number(selectedSupplierId)
  );

  const endTime = time?.toTime
    ? customTimeConverter(dayjs(time?.toTime, "HH:mm:ss").format("hh:mm:ss A"))
    : undefined;
  const { startDate: sd, endDate: ed, ...filteredWithoutDates } = filter;
  const {
    data: paymentHistoryData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetPaymentHistoryQuery({
    ...filteredWithoutDates,
    page: page,
    search: keyword,
    party: selectedCategory,
    ...applyDateFilter,
    supplierId: selectedSupplier?.dynamic_id,
    supplierKeyword: selectedSupplier?.dynamic_keyword,
  });

  // const {
  //   data: treaderGraphData,
  //   isLoading: traderGraphLoading,
  //   isError: isTraderGraphError,
  //   error: traderGraphError,
  // } = useGetTradersListQuery({
  //   ...filteredWithoutDates,
  //   page: page,
  //   search: keyword,
  //   ...applyDateFilter,
  //   supplierId: selectedSupplier?.dynamic_id,
  //   supplierKeyword: selectedSupplier?.dynamic_keyword,
  // });

  const {
    isError: getZoneListIsError,
    error: getZoneListError,
    data: getZoneListData,
  } = useGetZoneListQuery({
    supplierId: selectedSupplier?.dynamic_id,
    supplierKeyword: selectedSupplier?.dynamic_keyword,
  });

  const {
    isError: getDistrictListIsError,
    error: getDistrictListError,
    data: getDistrictListData,
  } = useGetDistrictListQuery({
    params: {
      zone: selectedZone,
      supplierId: selectedSupplier?.dynamic_id,
      supplierKeyword: selectedSupplier?.dynamic_keyword,
    },
  });

  const {
    isError: getBusinessUnitIsError,
    error: getBusinessUnitError,
    data: getBusinessUnitData,
  } = useGetBusinessUnitQuery({
    supplierId: selectedSupplier?.dynamic_id,
    supplierKeyword: selectedSupplier?.dynamic_keyword,
  });
  useError(isError, error, "Get statement data fail");
  // useError(isTraderGraphError, traderGraphError, "Get trader graph data fail");
  useError(getZoneListIsError, getZoneListError, "Get zone data fail");
  useError(
    getDistrictListIsError,
    getDistrictListError,
    "Get district data fail"
  );
  useError(
    getBusinessUnitIsError,
    getBusinessUnitError,
    "Get business unit data fail"
  );
  useError(isCompanyError, companyError, "Company get fail");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const handleStatementCancel = () => {
    handleFilter("startDate", undefined);
    handleFilter("endDate", undefined);
    setTime(undefined);
    setDateError(null);
    setEndDateError(null);
  };

  const handleSelectChange = (value: string) => {
    setSelectedCategory(value);
  };

  // useEffect(() => {
  //   refetch();
  // }, [selectedCategory]);

  const DateTimeSelector = (
    <div className="p-6 border rounded-lg shadow-md max-w-md mx-auto bg-white clear-button-hide">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-600 mb-1">From Date</label>
          <DatePicker
            value={
              filter?.startDate ? dayjs(filter?.startDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) => {
              const newStartDate = e
                ? dayjs(e).format(dbDateFormat)
                : undefined;

              // 🚫 Check if it's after the current end date
              if (
                newStartDate &&
                filter?.endDate &&
                dayjs(newStartDate).isAfter(dayjs(filter?.endDate))
              ) {
                handleFilter("startDate", undefined);
                setDateError("Start date cannot be later than end date.");
                return;
              }
              setEndDateError(null);
              setDateError(null);
              handleFilter("startDate", newStartDate);
            }}
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.formTime ? dayjs(time?.formTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) =>
              setTime((prev) => ({
                ...prev,
                formTime: dayjs(e).format("HH:mm:ss"),
              }))
            }
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
        {!!dateError && (
          <small className="text-red-500 block col-span-2">{dateError}</small>
        )}
        <div>
          <label className="block text-gray-600 mb-1">To Date</label>
          <DatePicker
            value={
              filter?.endDate ? dayjs(filter?.endDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) => {
              const newEndDate = e ? dayjs(e).format(dbDateFormat) : undefined;

              // 🚫 Check if it's before the current start date
              if (
                newEndDate &&
                filter?.startDate &&
                dayjs(newEndDate).isBefore(dayjs(filter?.startDate))
              ) {
                setDateError(null);
                setEndDateError(null);
                handleFilter("endDate", undefined);
                setEndDateError("End date cannot be earlier than start date.");
                return;
              }

              handleFilter("endDate", newEndDate);
              setEndDateError(null);
            }}
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.toTime ? dayjs(time?.toTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) => {
              setTime((prev) => ({
                ...prev,
                toTime: dayjs(e).format("HH:mm:ss"),
              }));
            }}
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>{" "}
        {!!endDateError && (
          <small className="text-red-500 block col-span-2">
            {endDateError}
          </small>
        )}
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <Button
          className="text-gray-600"
          onClick={() => {
            setOpenDropdown(false);
            handleStatementCancel();
            setApplyDateFilter({});
          }}
        >
          Reset
        </Button>
        <Button
          onClick={() => {
            setOpenDropdown(false);
            setDateError(null);
            setEndDateError(null);
            setApplyDateFilter({
              startDate: filter?.startDate
                ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
                : undefined,
              endDate: filter?.endDate
                ? `${filter?.endDate} ${endTime || "23:59:59"}`
                : undefined,
            });
          }}
          color="primary"
          className="text-gray-600"
        >
          Apply
        </Button>
        {/* <Button
          type="primary"
          className="!text-primary !bg-[#147AFF33]"
          onClick={() => setOpenDropdown(false)}
        >
          View Statement
        </Button> */}
      </div>
    </div>
  );

  const { page: fPage, limit: fLimit, ...filterData } = filter;

  const handleDownload = () => {
    downloadXlsx(
      API_URL_BSRM + BASE_API_BSRM.exportStatement,
      {
        ...filterData,
        page: page,
        search: keyword,
        startDate: filter?.startDate
          ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
          : undefined,
        endDate: filter?.endDate
          ? `${filter?.endDate} ${endTime || "23:59:59"}`
          : undefined,

        limit: 100000000000,
        party: selectedCategory,
        supplierId: selectedSupplier?.dynamic_id,
        supplierKeyword: selectedSupplier?.dynamic_keyword,
      },
      `sPay_statement_dealers${moment().format(
        "DD MMMM YYYY"
      )}_${moment().format("h.mm.ss A")}`,
      "transactions",
      (data: ExportStatementType["data"]["transactions"][0]) => {
        return {
          "Dealer Name": data?.merchant?.name ?? "-",
          "Dealer Code": data?.merchant?.dealerNo ?? "-",
          "Wallet Number": data?.merchant?.merchantMsisdn ?? "-",
          Zone: data?.merchant?.zone ?? "-",
          District: data?.merchant?.district ?? "-",
          "Business Unit": data?.businessUnit?.name ?? "-",
          Amount: numberWithCommas(Number(data?.amount) || 0)?.toString(),
          "Sheba Payment Date": data?.paymentCreatedAt
            ? `${moment(data?.paymentCreatedAt).utc().format("DD/MM/YYYY")}`
            : "-",
          "Sheba Payment Time": data?.paymentCreatedAt
            ? `${moment(data?.paymentCreatedAt).utc().format("hh:mm A")}`
            : "-",
          "Sheba Payment Status": data?.status ?? "-",
          "Sheba Transaction ID": data?.transactionId ?? "-",
          "Bank Transfer Date": data?.spayTransferAt
            ? `${moment(data?.spayTransferAt).utc().format("DD/MM/YYYY")}`
            : "-",
          "Bank Transfer Time": data?.spayTransferAt
            ? `${moment(data?.spayTransferAt).utc().format("hh:mm A")}`
            : "-",
          "Bank Transfer To": data?.businessUnit?.bankName ?? "-",
          "Bank Account Name": data?.businessUnit?.bankAccountName ?? "-",
          "Bank Account Number": data?.businessUnit?.bankAccountNo ?? "-",
          "Bank Transfer Status":
            capitalize(data?.mStatus === "success" ? "Success" : "pending") ??
            "Pending",
          "Bank Transaction ID": data?.bankTransactionId ?? "-",
          "BSRM MR Date": data?.erpCreatedAt
            ? `${moment(data?.erpCreatedAt).utc().format("DD/MM/YYYY")}`
            : "-",
          "BSRM MR Time": data?.erpCreatedAt
            ? `${moment(data?.erpCreatedAt).utc().format("hh:mm A")}`
            : "-",
          "BSRM MR Status":
            capitalize(
              data?.omrStatus === "success"
                ? "Success"
                : data?.omrStatus === "approved"
                ? "Approved"
                : data?.omrStatus === "failed"
                ? "Failed"
                : "pending"
            ) ?? "Pending",
          "BSRM MR ID": data?.omrId ?? "-",

          // "Transaction Time": data?.createdAt
          //   ? `${moment(data?.createdAt).utc().format("DD/MM/YYYY")} - ${moment(
          //       data?.createdAt
          //     )
          //       .utc()
          //       .format("hh:mm A")}`
          //   : "-",
        };
      }
    );
  };

  const handleDownloadTraders = () => {
    downloadXlsx(
      API_URL_BSRM + BASE_API_BSRM.exportStatement,
      {
        ...filterData,
        page: page,
        search: keyword,
        startDate: filter?.startDate
          ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
          : undefined,
        endDate: filter?.endDate
          ? `${filter?.endDate} ${endTime || "23:59:59"}`
          : undefined,

        limit: 100000000000,
        party: selectedCategory,
        supplierId: selectedSupplier?.dynamic_id,
        supplierKeyword: selectedSupplier?.dynamic_keyword,
      },
      `sPay_statement_traders${moment().format(
        "DD MMMM YYYY"
      )}_${moment().format("h.mm.ss A")}`,
      "transactions",
      (data: ExportStatementType["data"]["transactions"][0]) => {
        return {
          "Trader Name": data?.merchant?.name ?? "-",
          "Trader Code": data?.merchant?.dealerNo ?? "-",
          "Wallet Number": data?.merchant?.merchantMsisdn ?? "-",
          Zone: data?.merchant?.zone ?? "-",
          District: data?.merchant?.district ?? "-",
          "Business Unit": data?.businessUnit?.name ?? "-",
          Amount: numberWithCommas(Number(data?.amount) || 0)?.toString(),
          "Sheba Payment Date": data?.paymentCreatedAt
            ? `${moment(data?.paymentCreatedAt).utc().format("DD/MM/YYYY")}`
            : "-",
          "Sheba Payment Time": data?.paymentCreatedAt
            ? `${moment(data?.paymentCreatedAt).utc().format("hh:mm A")}`
            : "-",
          "Sheba Payment Status": data?.status ?? "-",
          "Sheba Transaction ID": data?.transactionId ?? "-",
          "Bank Transfer Date": data?.spayTransferAt
            ? `${moment(data?.spayTransferAt).utc().format("DD/MM/YYYY")}`
            : "-",
          "Bank Transfer Time": data?.spayTransferAt
            ? `${moment(data?.spayTransferAt).utc().format("hh:mm A")}`
            : "-",
          "Bank Transfer To": data?.businessUnit?.bankName ?? "-",
          "Bank Account Name": data?.businessUnit?.bankAccountName ?? "-",
          "Bank Account Number": data?.businessUnit?.bankAccountNo ?? "-",
          "Bank Transfer Status":
            capitalize(data?.mStatus === "success" ? "Success" : "pending") ??
            "Pending",
          "Bank Transaction ID": data?.bankTransactionId ?? "-",
          "BSRM MR Date": data?.erpCreatedAt
            ? `${moment(data?.erpCreatedAt).utc().format("DD/MM/YYYY")}`
            : "-",
          "BSRM MR Time": data?.erpCreatedAt
            ? `${moment(data?.erpCreatedAt).utc().format("hh:mm A")}`
            : "-",
          "BSRM MR Status":
            capitalize(
              data?.omrStatus === "success"
                ? "Success"
                : data?.omrStatus === "approved"
                ? "Approved"
                : data?.omrStatus === "failed"
                ? "Failed"
                : "pending"
            ) ?? "Pending",
          "BSRM MR ID": data?.omrId ?? "-",

          //   "Transaction Time": data?.createdAt
          // ? `${moment(data?.createdAt).utc().format("DD/MM/YYYY")} - ${moment(
          //     data?.createdAt
          //   )
          //     .utc()
          //     .format("hh:mm A")}`
          // : "-",
        };
      }
    );
  };

  const handleDownloadRetailers = () => {
    downloadXlsx(
      API_URL_BSRM + BASE_API_BSRM.exportStatement,
      {
        ...filterData,
        page: page,
        search: keyword,
        startDate: filter?.startDate
          ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
          : undefined,
        endDate: filter?.endDate
          ? `${filter?.endDate} ${endTime || "23:59:59"}`
          : undefined,
        limit: 100000000000,
        party: selectedCategory,
        supplierId: selectedSupplier?.dynamic_id,
        supplierKeyword: selectedSupplier?.dynamic_keyword,
      },
      `sPay_statement_retailers${moment().format(
        "DD MMMM YYYY"
      )}_${moment().format("h.mm.ss A")}`,
      "transactions",
      (data: ExportStatementType["data"]["transactions"][0]) => {
        return {
          "Register Merchant Name": data?.merchant?.name ?? "-",
          "Register Merchant Code": data?.merchant?.dealerNo ?? "-",
          "Register Merchant Wallet Number":
            data?.merchant?.merchantMsisdn ?? "-",
          "Retailer Name": data?.retailer?.profile?.merchantName ?? "-",
          "Retailer Wallet Number":
            data?.retailer?.profile?.merchantNumber ?? "-",
          Zone: data?.merchant?.zone ?? "-",
          District: data?.merchant?.district ?? "-",
          "Business Unit": data?.businessUnit?.name ?? "-",
          Amount: numberWithCommas(Number(data?.amount) || 0)?.toString(),
          "Sheba Payment Date": data?.paymentCreatedAt
            ? `${moment(data?.paymentCreatedAt).utc().format("DD/MM/YYYY")}`
            : "-",
          "Sheba Payment Time": data?.paymentCreatedAt
            ? `${moment(data?.paymentCreatedAt).utc().format("hh:mm A")}`
            : "-",
          "Sheba Payment Status": data?.status ?? "-",
          "Sheba Transaction ID": data?.transactionId ?? "-",
          "Bank Transfer Date": data?.spayTransferAt
            ? `${moment(data?.spayTransferAt).utc().format("DD/MM/YYYY")}`
            : "-",
          "Bank Transfer Time": data?.spayTransferAt
            ? `${moment(data?.spayTransferAt).utc().format("hh:mm A")}`
            : "-",
          "Bank Transfer To": data?.businessUnit?.bankName ?? "-",
          "Bank Account Name": data?.businessUnit?.bankAccountName ?? "-",
          "Bank Account Number": data?.businessUnit?.bankAccountNo ?? "-",
          "Bank Transfer Status":
            capitalize(data?.mStatus === "success" ? "Success" : "pending") ??
            "Pending",
          "Bank Transaction ID": data?.bankTransactionId ?? "-",
          "BSRM MR Date": data?.erpCreatedAt
            ? `${moment(data?.erpCreatedAt).utc().format("DD/MM/YYYY")}`
            : "-",
          "BSRM MR Time": data?.erpCreatedAt
            ? `${moment(data?.erpCreatedAt).utc().format("hh:mm A")}`
            : "-",
          "BSRM MR Status":
            capitalize(
              data?.omrStatus === "success"
                ? "Success"
                : data?.omrStatus === "approved"
                ? "Approved"
                : data?.omrStatus === "failed"
                ? "Failed"
                : "pending"
            ) ?? "Pending",
          "BSRM MR ID": data?.omrId ?? "-",

          // "Transaction Time": data?.createdAt
          // ? `${moment(data?.createdAt).utc().format("DD/MM/YYYY")} - ${moment(
          //     data?.createdAt
          //   )
          //     .utc()
          //     .format("hh:mm A")}`
          // : "-",
        };
      }
    );
  };

  const handleZoneChange = (val: string) => {
    setSelectedZone(val);
    handleFilter("zone", val); // Optional: if you're doing filter logic
  };

  // Optional: handle district change
  const handleDistrictChange = (val: string) => {
    handleFilter("district", val);
  };

  useEffect(() => {
    const dynamicKeys = companyData?.data?.dynamicKeys;
    if (Array.isArray(dynamicKeys) && dynamicKeys.length > 0) {
      const firstId = String(dynamicKeys[0].id);
      setSelectedSupplierId((prev) => prev ?? firstId);
    }
  }, [companyData]);

  const columns: ColumnsType<GetSupplierPaymentHistoryResponseType> = [
    ...(selectedCategory === "dealer"
      ? [
          {
            title: <div style={{ width: "200px" }}>Dealer Name</div>,

            render: (val: any) => {
              return (
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full">
                    <img
                      src={val?.merchant?.profile?.logoImage || emptyUser}
                      alt="Dealer"
                      className="rounded-full w-full h-full object-cover"
                    />
                  </div>

                  <span className="flex-1">
                    {val?.merchant?.name ? val?.merchant?.name : "N/A"}
                  </span>
                </div>
              );
            },
          },
        ]
      : []),

    ...(selectedCategory === "dealer"
      ? [
          {
            title: <div className="text-nowrap">Dealer Code</div>,
            align: "center" as const,
            render: (val: any) => {
              return val?.merchant?.dealerNo === null ||
                val?.merchant?.dealerNo === undefined ||
                val?.merchant?.dealerNo === ""
                ? "N/A"
                : val?.merchant?.dealerNo;
            },
          },
        ]
      : []),

    ...(selectedCategory === "dealer"
      ? [
          {
            title: <div className="text-nowrap">Wallet Number</div>,
            align: "center" as const,
            render: (val: any) => {
              return val?.merchant?.merchantMsisdn === null ||
                val?.merchant?.merchantMsisdn === undefined ||
                val?.merchant?.merchantMsisdn === ""
                ? "N/A"
                : val?.merchant?.merchantMsisdn;
            },
          },
        ]
      : []),

    ...(selectedCategory === "trader"
      ? [
          {
            title: <div style={{ width: "200px" }}>Trader Name</div>,

            render: (val: any) => {
              return (
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full">
                    <img
                      src={val?.merchant?.profile?.logoImage || emptyUser}
                      alt="Dealer"
                      className="rounded-full w-full h-full object-cover"
                    />
                  </div>

                  <span className="flex-1">
                    {val?.merchant?.name ? val?.merchant?.name : "N/A"}
                  </span>
                </div>
              );
            },
          },
        ]
      : []),

    ...(selectedCategory === "trader"
      ? [
          {
            title: <div className="text-nowrap">Trader Code</div>,
            align: "center" as const,
            render: (val: any) => {
              return val?.merchant?.dealerNo === null ||
                val?.merchant?.dealerNo === undefined ||
                val?.merchant?.dealerNo === ""
                ? "N/A"
                : val?.merchant?.dealerNo;
            },
          },
        ]
      : []),

    ...(selectedCategory === "trader"
      ? [
          {
            title: <div className="text-nowrap">Wallet Number</div>,
            align: "center" as const,
            render: (val: any) => {
              return val?.merchant?.merchantMsisdn === null ||
                val?.merchant?.merchantMsisdn === undefined ||
                val?.merchant?.merchantMsisdn === ""
                ? "N/A"
                : val?.merchant?.merchantMsisdn;
            },
          },
        ]
      : []),

    ...(selectedCategory === "retailer"
      ? [
          {
            title: <div style={{ width: "200px" }}>Register Merchant Name</div>,

            render: (val: any) => {
              return (
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full">
                    <img
                      src={val?.merchant?.profile?.logoImage || emptyUser}
                      alt="Dealer"
                      className="rounded-full w-full h-full object-cover"
                    />
                  </div>

                  <span className="flex-1">
                    {val?.merchant?.name ? val?.merchant?.name : "N/A"}
                  </span>
                </div>
              );
            },
          },
        ]
      : []),

    ...(selectedCategory === "retailer"
      ? [
          {
            title: <div className="text-nowrap">Register Merchant Code</div>,
            align: "center" as const,
            render: (val: any) => {
              return val?.merchant?.dealerNo === null ||
                val?.merchant?.dealerNo === undefined ||
                val?.merchant?.dealerNo === ""
                ? "N/A"
                : val?.merchant?.dealerNo;
            },
          },
        ]
      : []),

    ...(selectedCategory === "retailer"
      ? [
          {
            title: (
              <div className="text-nowrap">Register Merchant Wallet Number</div>
            ),
            align: "center" as const,
            render: (val: any) => {
              return val?.merchant?.merchantMsisdn === null ||
                val?.merchant?.merchantMsisdn === undefined ||
                val?.merchant?.merchantMsisdn === ""
                ? "N/A"
                : val?.merchant?.merchantMsisdn;
            },
          },
        ]
      : []),

    ...(selectedCategory === "retailer"
      ? [
          {
            title: <div style={{ width: "200px" }}>Retailer Name</div>,
            render: (val: any) => (
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 rounded-full">
                  <img
                    src={val?.retailer?.profile?.logoImage || emptyUser}
                    alt="Retailer_Image"
                    className="rounded-full w-full h-full object-cover"
                  />
                </div>
                <span className="flex-1">
                  {val?.retailer?.profile?.merchantName
                    ? val?.retailer?.profile?.merchantName
                    : "N/A"}
                </span>
              </div>
            ),
          },
        ]
      : []),

    ...(selectedCategory === "retailer"
      ? [
          {
            title: <div className="text-nowrap">Retailer Wallet Number</div>,
            align: "center" as const,
            render: (val: any) => {
              return val?.retailer?.profile?.merchantNumber === null ||
                val?.retailer?.profile?.merchantNumber === undefined ||
                val?.retailer?.profile?.merchantNumber === ""
                ? "N/A"
                : val?.retailer?.profile?.merchantNumber;
            },
          },
        ]
      : []),

    {
      title: "Zone",
      dataIndex: "merchant",
      align: "center",
      render: (val) => {
        return val?.zone === null || val?.zone === undefined || val?.zone === ""
          ? "N/A"
          : val.zone;
      },
    },
    {
      title: "District",
      dataIndex: "merchant",
      align: "center",
      render: (val) => {
        return val?.district === null ||
          val?.district === undefined ||
          val?.district === ""
          ? "N/A"
          : val.district;
      },
    },

    {
      title: <div className="text-nowrap">Business Unit</div>,
      render: (val) =>
        val?.businessUnit?.name ? val?.businessUnit?.name : "N/A",
    },

    {
      title: "Amount",
      dataIndex: "amount",
      align: "center",
      render: (val) => formatSouthAsianNumber(val),
    },

    {
      title: <div className="text-nowrap">Sheba Payment Time</div>,
      align: "center",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.paymentCreatedAt
              ? moment(val?.paymentCreatedAt).format("DD/MM/YYYY")
              : "-"}{" "}
            -{" "}
            {val?.paymentCreatedAt
              ? moment(val?.paymentCreatedAt).format("hh:mm A")
              : "-"}
          </span>
        );
      },
    },

    {
      title: <div className="text-nowrap">Sheba Payment Status</div>,
      dataIndex: "status",
      align: "center",
      render: (status: any) => (
        <Tag
          color={status === "success" ? "success" : "warning"}
          className="rounded-full"
          bordered={false}
        >
          {status === "success" ? "Success" : "Pending"}
        </Tag>
      ),
    },

    {
      title: "Bank Transfer Time",
      align: "center",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.spayTransferAt
              ? moment(val?.spayTransferAt).format("DD/MM/YYYY")
              : "-"}{" "}
            -{" "}
            {val?.spayTransferAt
              ? moment(val?.spayTransferAt).format("hh:mm A")
              : "-"}
          </span>
        );
      },
    },

    {
      title: <div className="text-nowrap">Bank Transfer To</div>,
      render: (val) =>
        val?.businessUnit?.bankName ? val?.businessUnit?.bankName : "N/A",
    },
    {
      title: <span className="text-nowrap">Bank Transfer Status</span>,
      dataIndex: "mStatus",
      align: "center",
      render: (status) => {
        // Handle null or empty values as "Pending"
        if (status === null || status === undefined || status === "") {
          status = "pending";
        }

        return (
          <Tag
            color={
              status === "success"
                ? "success"
                : status === "failed"
                ? "error"
                : "warning"
            }
            className="rounded-full"
            bordered={false}
          >
            {status === "success"
              ? "Success"
              : status === "failed"
              ? "Failed"
              : "Pending"}
          </Tag>
        );
      },
    },
    {
      title: <span className="text-nowrap">BSRM MR Time</span>,
      align: "center",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.erpCreatedAt
              ? moment(val?.erpCreatedAt).format("DD/MM/YYYY")
              : "-"}{" "}
            -{" "}
            {val?.erpCreatedAt
              ? moment(val?.erpCreatedAt).format("hh:mm A")
              : "-"}
          </span>
        );
      },
    },
    {
      title: <span className="text-nowrap">BSRM MR Status</span>,
      dataIndex: "omrStatus",
      align: "center",
      render: (status: any) => {
        // If omrStatus is null, undefined, or empty string → show N/A
        if (status === null || status === undefined || status === "") {
          return (
            <Tag color={"warning"} className="rounded-full" bordered={false}>
              Pending
            </Tag>
          );
        }

        return (
          <Tag
            color={
              status === "success"
                ? "success"
                : status === "failed"
                ? "error"
                : "warning"
            }
            className="rounded-full"
            bordered={false}
          >
            {capitalize(status)}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      render: (val) => (
        <Button
          type="default"
          className="hover:!border-slate-200"
          onClick={() => handleOpen({ type: "VIEW", data: val })}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <>
      {open.type === "VIEW" && (
        <StatementDetailsModal
          open={open.open}
          type={open.type}
          data={open.data}
          selectedCategory={selectedCategory}
          handleClose={handleClose}
        />
      )}

      <SidebarLayout title="Statement">
        <div className="mt-4">
          <CustomTable
            loading={isLoading}
            limit={limit}
            page={page}
            columns={columns}
            total={paymentHistoryData?.data?.metadata?.total || 0}
            dataSource={paymentHistoryData?.data?.transactions || []}
            handleLimit={handleLimit}
            handlePagination={handlePagination}
            filterSection={
              <>
                <Select
                  placeholder="Select Supplier Key"
                  showSearch
                  className="!min-w-[110px]"
                  suffixIcon={<SelectDownIcon />}
                  style={{ width: "100%" }}
                  // allowClear
                  optionFilterProp="label"
                  options={companyData?.data?.dynamicKeys?.map((item: any) => ({
                    label: item.dynamic_key_name,
                    value: String(item.id),
                  }))}
                  value={selectedSupplierId} // 👈 controlled value
                  onChange={(value) => setSelectedSupplierId(value)}
                />

                <Select
                  showSearch
                  suffixIcon={<SelectDownIcon />}
                  optionFilterProp="label"
                  defaultValue="dealer"
                  onChange={handleSelectChange}
                  options={[
                    { value: "dealer", label: "Dealers" },
                    { value: "trader", label: "Traders" },
                    { value: "retailer", label: "Retailers" },
                  ]}
                />

                <Dropdown
                  overlay={DateTimeSelector}
                  trigger={["click"]}
                  open={openDropdown}
                  onOpenChange={setOpenDropdown}
                >
                  <IconButton className="flex items-center text-[#7a8799]">
                    {applyDateFilter &&
                    (filter?.startDate ||
                      filter?.endDate ||
                      time?.formTime ||
                      time?.toTime) ? (
                      <>
                        {filter?.startDate && (
                          <span>
                            {dayjs(filter.startDate).format("DD/MM/YYYY")}
                            {time?.formTime &&
                              ` ${dayjs(time.formTime, "HH:mm:ss").format(
                                "hh:mm A"
                              )}`}
                          </span>
                        )}
                        {" - "}
                        {filter?.endDate && (
                          <span>
                            {dayjs(filter.endDate).format("DD/MM/YYYY")}
                            {time?.toTime &&
                              ` ${dayjs(time.toTime, "HH:mm:ss").format(
                                "hh:mm A"
                              )}`}
                          </span>
                        )}
                      </>
                    ) : (
                      "Date & Time"
                    )}
                  </IconButton>
                </Dropdown>

                <Select
                  placeholder="Zone"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="min-w-[100px]"
                  options={
                    getZoneListData?.data?.rows?.map((zone: string) => ({
                      label: zone,
                      value: zone,
                    })) || []
                  }
                  onChange={(val) => {
                    handleZoneChange(val);
                    handleDistrictChange(undefined as unknown as string);
                  }}
                />

                <Select
                  placeholder="District"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="min-w-[100px]"
                  disabled={!selectedZone}
                  value={filter?.district}
                  options={
                    getDistrictListData?.data?.rows?.map(
                      (district: string) => ({
                        label: district,
                        value: district,
                      })
                    ) || []
                  }
                  onChange={handleDistrictChange}
                />

                <Select
                  placeholder="Business Unit"
                  showSearch
                  suffixIcon={<SelectDownIcon />}
                  options={
                    getBusinessUnitData?.data?.rows?.map((unit) => ({
                      label: unit.name,
                      value: unit.id,
                    })) || []
                  }
                  className="min-w-[100px]"
                  allowClear
                  onChange={(val) => handleFilter("businessUnit", val)}
                />

                <Select
                  placeholder="Sheba Payment Status"
                  suffixIcon={<SelectDownIcon />}
                  value={filter?.spayStatus ?? undefined}
                  onChange={(value) => handleFilter("spayStatus", value)}
                  options={[
                    { label: "Success", value: "success" },
                    { label: "Pending", value: "failed" },
                  ]}
                  className="min-w-[100px]"
                  allowClear
                />

                <Select
                  placeholder="	Bank Transfer Status"
                  suffixIcon={<SelectDownIcon />}
                  value={filter?.bankStatus ?? undefined}
                  onChange={(value) => handleFilter("bankStatus", value)}
                  options={[
                    { label: "Success", value: "success" },
                    { label: "Pending", value: "pending" },
                  ]}
                  className="min-w-[100px]"
                  allowClear
                />

                <Select
                  placeholder="BSRM MR Status"
                  suffixIcon={<SelectDownIcon />}
                  value={filter?.omrStatus ?? undefined}
                  onChange={(value) => handleFilter("omrStatus", value)}
                  options={[
                    { label: "Success", value: "success" },
                    { label: "Pending", value: "pending" },
                  ]}
                  className="min-w-[100px]"
                  allowClear
                />

                <Input
                  placeholder={`Search ${
                    selectedCategory === "trader"
                      ? "trader’s name or code"
                      : selectedCategory === "retailer"
                      ? "retailer’s name or wallet"
                      : "dealer’s name or code"
                  }`}
                  className="text-[#AFBACA] !w-[280px]"
                  prefix={<SearchIcon />}
                  onChange={handleSearch}
                  allowClear
                />

                <Button
                  type="primary"
                  className="middle-button"
                  loading={excelLoading}
                  onClick={
                    selectedCategory === "dealer"
                      ? handleDownload
                      : selectedCategory === "trader"
                      ? handleDownloadTraders
                      : handleDownloadRetailers
                  }
                >
                  Export
                </Button>
              </>
            }
          />
        </div>
      </SidebarLayout>
    </>
  );
}
